/**
 * FeaturedPickLab
 *
 * Two Featured Picks (ML + Over/Under), each its own AI debate. The two summary
 * cards ACT AS the selector: click a card to load its debate in the lab beneath.
 * A third (Parlay) card slots into the same row when that's persisted.
 *
 * Tufte: sparklines (no axes, signal-color end dot, one faint reference line),
 * small multiples, data-ink only, direct labels, color = signal.
 */

import React, { useEffect, useState } from 'react';
import api from '../../services/api';

const MARKET_LABEL = { win_loss: 'ML', total_score: 'OVER / UNDER' };

function chosenCandidate(debate) {
  const cands = debate.candidates || [];
  return cands.find((c) => c.pick_label === debate.final_pick_label) || cands[0] || null;
}

function sportLabel(sport) {
  return (sport || '').replace('ncaa_f', 'ncaaf').replace('ncaa_b', 'ncaab').toUpperCase();
}

// The cross-sport featured debate (market 'featured') labels by its pick's
// sport and market, e.g. "NHL · OVER / UNDER".
function marketLabel(m, debate) {
  if (m === 'featured' && debate) {
    const c = chosenCandidate(debate);
    if (c) {
      return `${sportLabel(c.sport)} · ${c.pick_type === 'game_total' ? 'OVER / UNDER' : 'ML'}`;
    }
    return 'FEATURED';
  }
  return MARKET_LABEL[m] || m;
}

function matchupOf(debate) {
  const c = chosenCandidate(debate);
  return c ? `${c.away_team_name} @ ${c.home_team_name}` : '';
}

// ---------------------------------------------------------------------------
// Tufte sparkline: bare line, one faint reference line, a single end dot whose
// color = signal (above/below the baseline). No axes, no gridlines, no labels.
// ---------------------------------------------------------------------------
function Sparkline({ values = [], baseline = 0, width = 168, height = 40 }) {
  if (!values || values.length < 2) return null;
  const pad = 5;
  const min = Math.min(...values, baseline);
  const max = Math.max(...values, baseline);
  const range = max - min || 1;
  const x = (i) => pad + (i * (width - 2 * pad)) / (values.length - 1);
  const y = (v) => height - pad - ((v - min) / range) * (height - 2 * pad);
  const pts = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const last = values[values.length - 1];
  const endColor = last >= baseline ? '#34d399' : '#f87171';
  const baseY = y(baseline).toFixed(1);

  return (
    <svg width={width} height={height} className="block">
      <line
        x1={pad} y1={baseY} x2={width - pad} y2={baseY}
        stroke="#64748b" strokeOpacity="0.5" strokeWidth="1" strokeDasharray="2 3"
      />
      <polyline
        points={pts} fill="none" stroke="#94a3b8" strokeWidth="1.5"
        strokeLinejoin="round" strokeLinecap="round"
      />
      <circle cx={x(values.length - 1)} cy={y(last)} r="3" fill={endColor} />
    </svg>
  );
}

function sparkCaption(spark) {
  if (!spark) return '';
  const unit = spark.unit || 'run';
  return spark.kind === 'run_diff'
    ? `${unit} differential · last 10`
    : `total ${unit}s vs ${spark.baseline} · last 10`;
}

// ---------------------------------------------------------------------------
// Pick card — the hero summary AND the selector. Selected card glows gold.
// ---------------------------------------------------------------------------
function PickCard({ debate, selected, onClick }) {
  const conf = debate.confidence;
  const spark = debate.sparkline;
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'text-left w-full glass-card p-4 transition-all duration-150 ' +
        (selected
          ? 'ring-2 ring-amber-400/70 border-amber-400/40'
          : 'opacity-70 hover:opacity-100 hover:ring-1 hover:ring-border')
      }
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-widest text-text-secondary">
          {marketLabel(debate.market, debate)}
        </span>
        {debate.agreement ? (
          <span className="text-[10px] font-semibold text-accent-green">✓ converged</span>
        ) : (
          <span className="text-[10px] font-semibold text-amber-400">no consensus</span>
        )}
      </div>

      <div className="text-xl font-bold text-text-primary leading-tight">
        {debate.final_pick_label || '—'}
      </div>
      <div className="flex items-center gap-1.5 mt-1 mb-3">
        {debate.away_logo && (
          <img src={debate.away_logo} alt="" className="w-5 h-5 object-contain" />
        )}
        <span className="text-xs text-text-secondary">{matchupOf(debate)}</span>
        {debate.home_logo && (
          <img src={debate.home_logo} alt="" className="w-5 h-5 object-contain" />
        )}
      </div>

      <div className="flex items-end justify-between gap-3">
        <div>
          {spark && <Sparkline values={spark.values} baseline={spark.baseline} />}
          {spark && (
            <div className="text-[9px] text-text-secondary mt-1">{sparkCaption(spark)}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-3xl font-bold text-accent-green leading-none">
            {conf != null ? conf.toFixed(0) : '—'}
          </div>
          <div className="text-[9px] uppercase tracking-wide text-text-secondary mt-0.5">
            conf / 10
          </div>
          {conf != null && (
            <div className="mt-1 h-1 w-14 rounded-full bg-bg-card overflow-hidden ml-auto">
              <div
                className="h-full bg-accent-green/70"
                style={{ width: `${(conf / 10) * 100}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// The lab — the selected pick's candidates + AI debate thread + verdict.
// ---------------------------------------------------------------------------
function CandidateChip({ c, chosen }) {
  const conf = c.avg_confidence;
  const rec = c.rule_record;
  return (
    <div
      className={
        'rounded-md border p-2.5 text-xs ' +
        (chosen ? 'border-amber-400/50 bg-amber-400/[0.04]' : 'border-border/40')
      }
    >
      <div className="flex items-center justify-between gap-1">
        <span className="font-semibold text-text-primary truncate">
          {c.sport && (
            <span className="text-[9px] text-text-secondary mr-1">{sportLabel(c.sport)}</span>
          )}
          {c.pick_label}
        </span>
        {chosen && (
          <span className="text-[8px] font-bold uppercase text-amber-300 shrink-0">pick</span>
        )}
      </div>
      <div className="text-text-secondary truncate mt-0.5">
        {c.away_team_name} @ {c.home_team_name}
      </div>
      {conf != null && (
        <div className="text-text-secondary mt-0.5">conf {Number(conf).toFixed(2)}</div>
      )}
      {rec && (
        <div className="text-text-secondary mt-0.5">
          record {rec[0]}/{rec[1]} ({Math.round((rec[0] / rec[1]) * 100)}%)
        </div>
      )}
    </div>
  );
}

function DebateThread({ transcript }) {
  return (
    <div className="space-y-2">
      {transcript.map((turn, idx) => {
        const isFC = ['forecast_champion', 'claude'].includes(turn.speaker);
        return (
          <div key={idx} className={'flex ' + (isFC ? 'justify-start' : 'justify-end')}>
            <div
              className={
                'max-w-[85%] rounded-lg p-3 text-sm ' +
                (isFC
                  ? 'bg-purple-500/10 border border-purple-500/30 rounded-tl-none'
                  : 'bg-blue-500/10 border border-blue-500/30 rounded-tr-none')
              }
            >
              <div
                className={
                  'text-[10px] uppercase font-semibold mb-1 tracking-wide ' +
                  (isFC ? 'text-purple-300' : 'text-blue-300')
                }
              >
                {isFC ? 'FORECAST CHAMPION' : 'MVP MATRIX'}{' '}
                <span className="text-text-secondary font-normal">· turn {turn.turn}</span>
              </div>
              <div className="text-text-primary whitespace-pre-line">
                {(turn.content || '').replace(/\*\*/g, '').trim()}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DebateLab({ debate }) {
  if (!debate) return null;
  const cands = debate.candidates || [];
  return (
    <div className="glass-card p-4 space-y-4">
      <div className="flex items-baseline justify-between">
        <h4 className="font-semibold text-text-primary">
          {marketLabel(debate.market, debate)} — the debate
        </h4>
        <span className="text-xs text-text-secondary">
          {debate.agreement ? (
            <span className="text-accent-green">✓ converged ({debate.confidence}/10)</span>
          ) : (
            <span className="text-amber-400">no consensus</span>
          )}
        </span>
      </div>

      {cands.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-text-secondary mb-1.5">
            candidates considered
          </div>
          <div className="grid grid-cols-3 gap-2">
            {cands.slice(0, 3).map((c, i) => (
              <CandidateChip
                key={c.pick_label || i}
                c={c}
                chosen={c.pick_label === debate.final_pick_label}
              />
            ))}
          </div>
        </div>
      )}

      <DebateThread transcript={debate.transcript || []} />

      {debate.final_pick_label && (
        <div className="text-xs text-text-secondary text-center italic border-t border-border/40 pt-3">
          Closed on:{' '}
          <span className="text-text-primary font-semibold">{debate.final_pick_label}</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Parlay — the debate-vetted 3-5 leg ticket, as a third selector card + its lab.
// ---------------------------------------------------------------------------
function ParlayCard({ parlay, selected, onClick }) {
  const pct = parlay.combined_prob != null ? Math.round(parlay.combined_prob) : null;
  const legs = parlay.legs || [];
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'text-left w-full glass-card p-4 transition-all duration-150 ' +
        (selected
          ? 'ring-2 ring-amber-400/70 border-amber-400/40'
          : 'opacity-70 hover:opacity-100 hover:ring-1 hover:ring-border')
      }
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-widest text-text-secondary">PARLAY</span>
        {parlay.agreement ? (
          <span className="text-[10px] font-semibold text-accent-green">✓ converged</span>
        ) : (
          <span className="text-[10px] font-semibold text-amber-400">no consensus</span>
        )}
      </div>
      <div className="flex items-end justify-between mb-2">
        <div>
          <div className="text-3xl font-bold text-accent-green leading-none">
            {pct != null ? `${pct}%` : '—'}
          </div>
          <div className="text-[9px] uppercase tracking-wide text-text-secondary mt-0.5">to hit</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-text-primary leading-none">
            {parlay.leg_count ?? legs.length}
          </div>
          <div className="text-[9px] uppercase tracking-wide text-text-secondary mt-0.5">legs</div>
        </div>
      </div>
      <div className="text-xs text-text-secondary truncate">
        {legs.map((l) => l.label).join('  +  ') || '—'}
      </div>
    </button>
  );
}

function ParlayLab({ parlay }) {
  const legs = parlay.legs || [];
  const pct = parlay.combined_prob != null ? Math.round(parlay.combined_prob) : null;
  return (
    <div className="glass-card p-4 space-y-4">
      <div className="flex items-baseline justify-between">
        <h4 className="font-semibold text-text-primary">
          The Parlay{pct != null && <span className="text-accent-green"> — {pct}% to hit</span>}
        </h4>
        <span className="text-xs text-text-secondary">
          {parlay.agreement ? (
            <span className="text-accent-green">✓ both analysts agreed</span>
          ) : (
            <span className="text-amber-400">no consensus</span>
          )}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {legs.map((l, i) => (
          <div key={i} className="rounded-md border border-accent-green/40 bg-accent-green/[0.04] p-2.5 text-xs">
            <div className="font-semibold text-text-primary">{l.label}</div>
            {l.matchup && <div className="text-text-secondary mt-0.5">{l.matchup}</div>}
            {l.p_hit != null && (
              <div className="text-accent-green font-semibold mt-0.5">{Math.round(l.p_hit * 100)}%</div>
            )}
          </div>
        ))}
      </div>

      <DebateThread transcript={parlay.transcript || []} />
    </div>
  );
}

// ---------------------------------------------------------------------------
export default function FeaturedPickLab({ sport = 'mlb' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setSelectedMarket(null);
    api
      .get(`/top-picks/featured-pick-lab?sport=${sport}`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) setErr(e.response?.data?.detail || 'Failed to load lab data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sport]);

  const debates = (data?.debates || []).filter((d) => (d.transcript || []).length > 0);

  // Default selection: ML if present, else the first debate.
  useEffect(() => {
    if (debates.length && !selectedMarket) {
      const ml = debates.find((d) => d.market === 'win_loss');
      setSelectedMarket(ml ? ml.market : debates[0].market);
    }
  }, [debates, selectedMarket]);

  if (loading) {
    return (
      <div className="glass-card p-4 animate-pulse">
        <div className="h-4 bg-bg-card rounded w-1/3 mb-3" />
        <div className="grid grid-cols-2 gap-3">
          <div className="h-32 bg-bg-card rounded" />
          <div className="h-32 bg-bg-card rounded" />
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="glass-card bg-red-500/10 border-red-500/30 p-3 text-sm text-red-400">
        {err}
      </div>
    );
  }

  if (!debates.length) {
    return (
      <div className="glass-card p-4 text-sm text-text-secondary text-center">
        No featured-pick debates yet for {data?.date || 'today'}.
      </div>
    );
  }

  const parlay = data?.parlay;
  const selected = debates.find((d) => d.market === selectedMarket) || debates[0];
  const showParlay = selectedMarket === 'parlay' && parlay;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h3 className="text-lg font-semibold text-text-primary">
          Featured Picks{' '}
          <span className="text-xs text-text-secondary font-normal ml-1">
            ({sport === 'all' ? 'all sports' : sportLabel(sport)} — {data.date})
          </span>
        </h3>
        <div className="text-xs text-text-secondary">tap a card to open it</div>
      </div>

      <div className={'grid gap-3 ' + (parlay ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2')}>
        {debates.map((d) => (
          <PickCard
            key={d.market}
            debate={d}
            selected={d.market === selectedMarket}
            onClick={() => setSelectedMarket(d.market)}
          />
        ))}
        {parlay && (
          <ParlayCard
            parlay={parlay}
            selected={selectedMarket === 'parlay'}
            onClick={() => setSelectedMarket('parlay')}
          />
        )}
      </div>

      {showParlay ? <ParlayLab parlay={parlay} /> : <DebateLab debate={selected} />}
    </div>
  );
}
